import { useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  BookCheck,
  BrainCircuit,
  CalendarClock,
  CheckCircle2,
  FileDown,
  Gift,
  PieChart,
  RefreshCw,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import {
  abrirPdfFactura,
  useActualizarCostes,
  useCostes,
  useLlevarAlLibro,
  type Cargo,
  type Costes,
  type Mes,
} from '../api/costes';
import { AyudaGuia, Pregunta } from '../components/Ayuda';

// ── Formato ───────────────────────────────────────────────────────
// AWS cobra en dólares; Florent piensa en euros. Todo sale en € con el cambio
// de la última factura de AWS (así cuadra con lo que ve en sus facturas), y el
// importe en $ al lado cuando ayuda a reconocerlo.

const fmt = (n: number, dec = 2) =>
  n.toLocaleString('es-ES', { minimumFractionDigits: dec, maximumFractionDigits: dec });

function mesLabel(mes: string, corto = false): string {
  const [y, m] = mes.split('-').map(Number);
  if (!y || !m) return mes;
  const s = new Date(y, m - 1, 1).toLocaleDateString('es-ES', {
    month: corto ? 'short' : 'long',
    year: corto ? undefined : 'numeric',
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fechaLarga(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function haceCuanto(epoch: number): string {
  const min = Math.round((Date.now() / 1000 - epoch) / 60);
  if (min < 2) return 'ahora mismo';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  return h < 24 ? `hace ${h} h` : `hace ${Math.round(h / 24)} días`;
}

const diasHasta = (iso: string) =>
  Math.ceil((new Date(`${iso}T00:00:00`).getTime() - Date.now()) / 86400000);

const NOMBRE_ERROR: Record<string, string> = {
  ce: 'los costes por servicio',
  facturas: 'las facturas',
  plan: 'el plan gratuito',
  presupuesto: 'el presupuesto',
  cif: 'el CIF',
  almacenamiento: 'el espacio usado',
  alarmas: 'las alarmas',
};

const RESULTADO: Record<string, string> = {
  listo: 'Estudios hechos',
  rechazada: 'No eran facturas de luz',
  error: 'Fallos al leer',
  pending_review: 'Facturas por revisar',
  validation_failed: 'Facturas con importes raros',
  confirmed: 'Facturas confirmadas',
};

// ── Vista ─────────────────────────────────────────────────────────

export default function CostesView() {
  const { data, isLoading, isError, error, refetch } = useCostes();

  return (
    <div className="pb-16 md:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-extrabold tracking-tight text-deep">Costes</h1>
            <AyudaGuia seccion="costes" />
          </div>
          <p className="mt-1 max-w-2xl text-sm text-ink/55">
            Lo que cuesta la plataforma —web, portal y CRM— en AWS, sus facturas y lo que
            viene. Todo lo que harías entrando en AWS, sin entrar.
          </p>
        </div>
        {data && <Actualizar data={data} />}
      </div>

      {isLoading ? (
        <p className="mt-10 text-sm text-ink/50">
          Consultando AWS… la primera vez del día tarda unos segundos.
        </p>
      ) : isError || !data ? (
        <div className="mt-8 rounded-2xl border border-dashed border-red-300 bg-red-50 px-6 py-12 text-center">
          <AlertTriangle className="mx-auto text-red-500" size={28} strokeWidth={1.5} />
          <p className="mt-3 font-bold text-red-700">No se han podido cargar los costes</p>
          <p className="mt-1 text-sm text-red-600/80">{(error as Error)?.message}</p>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-full bg-red-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <Contenido c={data} />
      )}
    </div>
  );
}

function Contenido({ c }: { c: Costes }) {
  const k = c.cambio.usd_eur;
  const eur = (usd: number, dec = 2) => `${fmt(usd * k, dec)} €`;
  const errores = Object.keys(c.errores ?? {});

  return (
    <>
      {errores.length > 0 && (
        <div className="mt-5 flex gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <p>
            No se ha podido leer {errores.map((e) => NOMBRE_ERROR[e] ?? e).join(', ')}. El
            resto de la página es correcto. Si se repite mañana, avisa a Adrien.
          </p>
        </div>
      )}

      <Resumen c={c} eur={eur} />
      <Creditos c={c} eur={eur} />
      <EnQueSeVa c={c} eur={eur} />
      <Facturas c={c} />
      <Ia c={c} eur={eur} />
      <PorCliente c={c} eur={eur} />
      <LoQueViene c={c} eur={eur} />

      <p className="mt-8 text-xs text-ink/45">
        Los datos de AWS se actualizan como mucho una vez al día, porque cada actualización cuesta
        0,02 $. Cambio aplicado: 1 $ = {fmt(k, 4)} €
        {c.cambio.de_factura ? ', el de la última factura de AWS.' : ' (aproximado).'}
      </p>
    </>
  );
}

type Eur = (usd: number, dec?: number) => string;

// ── 1 · Resumen ───────────────────────────────────────────────────

function Resumen({ c, eur }: { c: Costes; eur: Eur }) {
  const avisos = c.alarmas.filter((a) => a.en_alarma);
  const usoMes = c.mes_actual?.uso ?? 0;
  const netoMes = c.mes_actual?.neto ?? 0;
  const conCreditos = c.plan?.tipo === 'FREE' && (c.plan?.creditos_restantes ?? 0) > 0;

  return (
    <Bloque
      icono={Wallet}
      titulo="Resumen del mes"
      pregunta={{
        titulo: '¿Por qué «lo que pagas» es distinto de lo gastado?',
        texto: (
          <>
            «Gastado» es lo que vale lo que has usado este mes. Mientras queden créditos del
            plan gratuito, AWS lo paga con ellos y tu factura sale a 0 €. Cuando se acaben, las
            dos cifras serán iguales, más el IVA. La casilla «Estado» vigila que todo funcione:
            si algo falla, lo verás ahí.
          </>
        ),
      }}
    >
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          label="Gastado este mes"
          value={eur(usoMes)}
          nota={`${fmt(usoMes)} $ hasta hoy`}
        />
        <Kpi
          label="Previsión a fin de mes"
          value={c.prevision ? eur(c.prevision.fin_de_mes) : '—'}
          nota={
            !c.prevision
              ? 'Aún sin datos'
              : c.prevision.base_mes_anterior
                ? 'Aproximada: el mes acaba de empezar'
                : `Al ritmo de ${eur(c.prevision.ritmo_diario, 3)} al día`
          }
        />
        <Kpi
          label="Lo que pagas tú"
          value={eur(netoMes)}
          nota={conCreditos ? 'Lo cubren los créditos' : 'Antes de IVA'}
        />
        <div
          className={clsx(
            'rounded-2xl border px-4 py-3',
            avisos.length ? 'border-red-300 bg-red-50' : 'border-deep/10 bg-surface',
          )}
        >
          <p
            className={clsx(
              'flex items-center gap-2 text-lg font-extrabold',
              avisos.length ? 'text-red-700' : 'text-deep',
            )}
          >
            {avisos.length ? <AlertTriangle size={20} /> : <CheckCircle2 size={20} className="text-brand" />}
            {avisos.length ? `${avisos.length} aviso${avisos.length > 1 ? 's' : ''}` : 'Todo en orden'}
          </p>
          <p className="text-[0.68rem] font-bold uppercase tracking-wider text-ink/45">Estado</p>
          {avisos.map((a) => (
            <p key={a.alarma} className="mt-1 text-xs text-red-700">
              Falla: {a.que.toLowerCase()}. Avisa a Adrien.
            </p>
          ))}
        </div>
      </div>
    </Bloque>
  );
}

// ── 2 · Créditos y plan gratuito ──────────────────────────────────

function Creditos({ c, eur }: { c: Costes; eur: Eur }) {
  const plan = c.plan;
  if (!plan) return null;
  if (plan.tipo !== 'FREE') {
    return (
      <Bloque icono={Gift} titulo="Plan de la cuenta">
        <p className="text-sm text-ink/70">
          La cuenta ya está en <strong>plan de pago</strong>: no caduca y se paga cada mes lo
          que se usa.
        </p>
      </Bloque>
    );
  }

  const dias = plan.caduca ? diasHasta(plan.caduca) : null;
  const completos = c.meses.filter((m) => m.mes < (c.mes_actual?.mes ?? '9999') && m.uso > 0);
  const medio = completos.length
    ? completos.slice(-3).reduce((s, m) => s + m.uso, 0) / Math.min(3, completos.length)
    : c.prevision?.fin_de_mes ?? 0;
  const gastoHastaCaducar = dias ? (medio * dias) / 30 : 0;
  const sobran = plan.creditos_restantes - gastoHastaCaducar;

  return (
    <Bloque
      icono={Gift}
      titulo="Créditos y plan gratuito"
      pregunta={{
        titulo: '¿Qué pasa si no se pasa a plan de pago?',
        texto: (
          <>
            AWS cierra la cuenta y la web, el portal y el CRM dejan de funcionar. Durante 90
            días se puede recuperar todo, pidiéndolo a su soporte y pagando lo pendiente;
            después se borra para siempre. Pasar a plan de pago <strong>no cuesta nada ni hace
            perder los créditos</strong>: se siguen usando hasta su fecha. Por eso conviene
            hacerlo cuanto antes, no en enero.
          </>
        ),
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Kpi
          label="Créditos que quedan"
          value={`${fmt(plan.creditos_restantes)} $`}
          nota={`≈ ${eur(plan.creditos_restantes)}`}
        />
        <Kpi
          label="Caduca el plan gratuito"
          value={plan.caduca ? fechaLarga(plan.caduca) : '—'}
          nota={dias !== null ? `Dentro de ${dias} días` : undefined}
        />
      </div>
      <p className="mt-3 text-sm text-ink/70">
        Al ritmo actual (unos {eur(medio)} al mes) gastarías {eur(gastoHastaCaducar)} de aquí a
        esa fecha.{' '}
        {sobran >= 0
          ? 'Los créditos sobran: lo que se acaba es el plazo, no el dinero.'
          : 'Los créditos se acabarían antes: a partir de ahí se paga con la tarjeta.'}
      </p>
      <Aviso>
        <strong>Antes del {plan.caduca ? fechaLarga(plan.caduca) : 'fin del plan'}</strong> hay
        que pasar la cuenta a plan de pago, con la tarjeta de Habitania. Se hace una vez y
        tarda cinco minutos; Adrien te acompaña.
      </Aviso>
    </Bloque>
  );
}

// ── 3 · En qué se va el dinero ────────────────────────────────────

function EnQueSeVa({ c, eur }: { c: Costes; eur: Eur }) {
  const meses = c.meses;
  const porDefecto =
    [...meses].reverse().find((m) => m.mes !== c.mes_actual?.mes && m.uso > 0) ??
    meses[meses.length - 1];
  const [elegido, setElegido] = useState<string | undefined>(undefined);
  const mes: Mes | undefined = meses.find((m) => m.mes === elegido) ?? porDefecto;
  const max = Math.max(0.01, ...meses.map((m) => m.uso));
  const maxServicio = Math.max(0.0001, ...(mes?.servicios ?? []).map((s) => s.importe));

  const bytes = c.almacenamiento?.bytes;
  const objetos = c.almacenamiento?.objetos;

  return (
    <Bloque
      icono={PieChart}
      titulo="En qué se va el dinero"
      pregunta={{
        titulo: '¿Por qué el día 1 de cada mes sube un poco?',
        texto: (
          <>
            El dominio (Route 53) se cobra entero el día 1, no repartido por días: son 0,50 $ de
            golpe. Es el único coste fijo. Todo lo demás depende del uso: si nadie entra en la
            web, casi no gasta. Toca un mes en el gráfico para ver su detalle.
          </>
        ),
      }}
    >
      {meses.length === 0 ? (
        <Vacio>
          AWS aún no tiene datos de costes. Aparecen unas 24 horas después del primer uso.
        </Vacio>
      ) : (
        <>
          <p className="text-xs font-bold uppercase tracking-wider text-ink/45">
            Gasto por mes (últimos 12)
          </p>
          <div className="mt-3 flex h-36 items-end gap-1.5 border-b border-deep/10" role="list">
            {meses.map((m) => {
              const enCurso = m.mes === c.mes_actual?.mes;
              const activo = m.mes === mes?.mes;
              return (
                <button
                  key={m.mes}
                  role="listitem"
                  type="button"
                  onClick={() => setElegido(m.mes)}
                  onMouseEnter={() => setElegido(m.mes)}
                  onFocus={() => setElegido(m.mes)}
                  title={`${mesLabel(m.mes)}: ${eur(m.uso)}${enCurso ? ' (en curso)' : ''}`}
                  aria-label={`${mesLabel(m.mes)}: ${eur(m.uso)}`}
                  className="group flex h-full flex-1 flex-col items-center justify-end"
                >
                  <span
                    className={clsx(
                      'w-full max-w-10 rounded-t-md transition-colors',
                      enCurso
                        ? 'bg-brand/35 bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(255,255,255,.6)_3px,rgba(255,255,255,.6)_5px)]'
                        : activo
                          ? 'bg-deep'
                          : 'bg-brand group-hover:bg-deep/80',
                    )}
                    style={{ height: `${Math.max(2, (m.uso / max) * 100)}%` }}
                  />
                </button>
              );
            })}
          </div>
          <div className="mt-1 flex gap-1.5">
            {meses.map((m) => (
              <span
                key={m.mes}
                className={clsx(
                  'flex-1 text-center text-[0.62rem]',
                  m.mes === mes?.mes ? 'font-bold text-deep' : 'text-ink/45',
                )}
              >
                {mesLabel(m.mes, true).replace('.', '')}
              </span>
            ))}
          </div>

          {mes && (
            <div className="mt-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-deep/10 pb-2">
                <h3 className="text-sm font-bold text-deep">
                  {mesLabel(mes.mes)}
                  {mes.mes === c.mes_actual?.mes && (
                    <span className="ml-2 text-xs font-semibold text-ink/45">en curso</span>
                  )}
                </h3>
                <p className="text-xs text-ink/55">
                  Gastado <strong className="text-deep">{eur(mes.uso)}</strong> · créditos{' '}
                  {eur(mes.creditos)} · pagado{' '}
                  <strong className="text-deep">{eur(mes.neto)}</strong>
                </p>
              </div>
              {mes.servicios.length === 0 ? (
                <p className="mt-3 text-sm text-ink/50">Sin gasto este mes.</p>
              ) : (
                <ul className="mt-3 space-y-3">
                  {mes.servicios.map((s) => (
                    <li key={s.servicio}>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-sm font-bold text-deep">{s.nombre}</span>
                        <span className="shrink-0 text-sm font-extrabold text-deep">
                          {s.importe * c.cambio.usd_eur < 0.01 ? '< 0,01 €' : eur(s.importe)}
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-soft">
                        <div
                          className="h-1.5 rounded-full bg-brand"
                          style={{ width: `${Math.max(1, (s.importe / maxServicio) * 100)}%` }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-ink/55">{s.que_es}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      {bytes != null && (
        <p className="mt-5 rounded-xl bg-soft px-4 py-3 text-sm text-ink/70">
          Fotos, documentos y facturas guardados:{' '}
          <strong className="text-deep">
            {bytes < 1e9 ? `${fmt(bytes / 1e6, 0)} MB` : `${fmt(bytes / 1e9, 1)} GB`}
          </strong>
          {objetos != null && ` en ${fmt(objetos, 0)} archivos`}. Guardar 1 GB cuesta unos 2
          céntimos al mes.
        </p>
      )}
    </Bloque>
  );
}

// ── 4 · Facturas de AWS ───────────────────────────────────────────

function Facturas({ c }: { c: Costes }) {
  const porMes = useMemo(() => {
    const m = new Map<string, Cargo[]>();
    for (const f of c.facturas) m.set(f.periodo, [...(m.get(f.periodo) ?? []), f]);
    return Array.from(m.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [c.facturas]);

  return (
    <Bloque
      icono={FileDown}
      titulo="Facturas de AWS"
      pregunta={{
        titulo: '¿Por qué llegan dos o tres facturas al mes? ¿Cuál es para la gestoría?',
        texto: (
          <>
            AWS factura desde dos empresas: <em>Amazon Web Services EMEA SARL</em> (casi todo)
            y <em>Amazon Web Services, Inc.</em> (la IA). Y la de AWS Inc. viene dos veces: en
            dólares y con el IVA español. Es el mismo cargo, no se paga dos veces. A la gestoría
            pásale la marcada «para la gestoría», o usa «Llevar al libro» y ya estará en
            Contabilidad. Mientras haya créditos, salen a 0.
          </>
        ),
      }}
    >
      {porMes.length === 0 ? (
        <Vacio>
          Todavía no hay facturas. AWS emite las del mes el día 2 del mes siguiente.
        </Vacio>
      ) : (
        <div className="space-y-5">
          {porMes.map(([periodo, cargos]) => (
            <section key={periodo}>
              <h3 className="border-b border-deep/10 pb-2 text-sm font-bold uppercase tracking-wider text-ink/55">
                {mesLabel(periodo)}
              </h3>
              <ul className="mt-3 space-y-2">
                {cargos.map((f) => (
                  <FilaFactura key={f.principal_id} f={f} k={c.cambio.usd_eur} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Bloque>
  );
}

function FilaFactura({ f, k }: { f: Cargo; k: number }) {
  const libro = useLlevarAlLibro();
  const [errorPdf, setErrorPdf] = useState<string | null>(null);
  const totalEur = f.total_eur ?? f.total_usd * k;
  const errorLibro = (libro.error as { response?: { data?: { message?: string } } } | null)
    ?.response?.data?.message;

  return (
    <li className="rounded-2xl border border-deep/10 bg-surface px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-deep">{f.entidad}</p>
          <p className="text-xs text-ink/55">
            Emitida el {fechaLarga(f.emitida)} · valía {fmt(f.subtotal_usd)} $
            {f.creditos_usd > 0 && `, créditos −${fmt(f.creditos_usd)} $`} · IVA{' '}
            {fmt(f.iva_pct, 0)} %
          </p>
        </div>
        <p className="shrink-0 text-lg font-extrabold text-deep">
          {fmt(totalEur)} €
          <span className="ml-1 text-xs font-semibold text-ink/45">({fmt(f.total_usd)} $)</span>
        </p>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {f.documentos.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() =>
              abrirPdfFactura(d.id).catch(() =>
                setErrorPdf('No se ha podido abrir el PDF. Inténtalo de nuevo en un momento.'),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-full border border-deep/15 px-3 py-1 text-xs font-bold text-deep transition hover:border-brand hover:text-brand"
          >
            <FileDown size={14} />
            {d.etiqueta}
          </button>
        ))}
        {f.total_usd > 0 &&
          (f.en_libro ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-leaf/50 px-3 py-1 text-xs font-bold text-deep">
              <BookCheck size={14} /> En el libro
            </span>
          ) : (
            <button
              type="button"
              disabled={libro.isPending}
              onClick={() => libro.mutate(f.principal_id)}
              className="inline-flex items-center gap-1.5 rounded-full bg-deep px-3 py-1 text-xs font-bold text-white transition hover:bg-brand disabled:opacity-50"
            >
              <BookCheck size={14} />
              {libro.isPending ? 'Llevando…' : 'Llevar al libro'}
            </button>
          ))}
      </div>
      {(errorPdf || errorLibro) && (
        <p className="mt-2 text-xs text-red-600">{errorPdf ?? errorLibro}</p>
      )}
    </li>
  );
}

// ── 5 · IA ────────────────────────────────────────────────────────

function Ia({ c, eur }: { c: Costes; eur: Eur }) {
  const ia = c.ia;
  const leidas = ia.este_mes.estudios + ia.este_mes.agencia;
  // Lo que AWS cobró de verdad por la IA el último mes con datos, para
  // contrastar con la estimación por tokens.
  const realIa = [...c.meses]
    .reverse()
    .find((m) => m.mes !== c.mes_actual?.mes && m.servicios.some((s) => s.nombre === 'IA que lee facturas'));
  const realIaUsd = realIa?.servicios
    .filter((s) => s.nombre === 'IA que lee facturas')
    .reduce((s, x) => s + x.importe, 0);

  return (
    <Bloque
      icono={BrainCircuit}
      titulo="Inteligencia artificial"
      pregunta={{
        titulo: '¿Qué hace la IA y qué datos guarda?',
        texto: (
          <>
            La IA (Claude, de Anthropic) lee dos cosas: las facturas de la luz que suben los
            clientes para el estudio de ahorro, y las facturas que subes tú en Contabilidad. Se
            paga por factura leída: fracciones de céntimo. De lo que lee, nunca se guardan el IBAN
            ni los datos bancarios; de las facturas de la luz, tampoco el nombre ni el NIF del
            titular.
          </>
        ),
      }}
    >
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          label="Facturas leídas este mes"
          value={fmt(leidas, 0)}
          nota={`${ia.este_mes.estudios} de clientes · ${ia.este_mes.agencia} de Contabilidad`}
        />
        <Kpi label="Coste este mes" value={eur(ia.este_mes.coste_usd, 3)} nota="Estimado" />
        <Kpi
          label="Coste por factura"
          value={`${fmt(ia.coste_medio_lectura_usd * k100(c), 2)} cént.`}
          nota="Media por lectura"
        />
        <Kpi
          label="100 estudios cuestan"
          value={eur(ia.coste_100_estudios_usd)}
          nota="Para calcular si crece"
        />
      </div>
      {Object.keys(ia.por_resultado).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {Object.entries(ia.por_resultado).map(([estado, n]) => (
            <span
              key={estado}
              className="rounded-full bg-soft px-3 py-1 text-xs font-semibold text-ink/70"
            >
              {RESULTADO[estado] ?? estado}: <strong className="text-deep">{n}</strong>
            </span>
          ))}
          <span className="self-center text-xs text-ink/45">(desde el principio)</span>
        </div>
      )}
      <p className="mt-3 text-xs text-ink/55">
        El coste se estima con lo que ocupa cada lectura.
        {realIa && realIaUsd != null &&
          ` Lo que AWS cobró de verdad por la IA en ${mesLabel(realIa.mes).toLowerCase()}: ${eur(realIaUsd, 3)}.`}
        {ia.lecturas_estimadas > 0 &&
          ` ${ia.lecturas_estimadas} lectura${ia.lecturas_estimadas > 1 ? 's' : ''} anterior${ia.lecturas_estimadas > 1 ? 'es' : ''} al 26/09 se valora${ia.lecturas_estimadas > 1 ? 'n' : ''} con la media.`}
      </p>
    </Bloque>
  );
}

/** Céntimos de euro por dólar: para enseñar fracciones de céntimo legibles. */
const k100 = (c: Costes) => c.cambio.usd_eur * 100;

// ── 6 · Coste por cliente ─────────────────────────────────────────

function PorCliente({ c, eur }: { c: Costes; eur: Eur }) {
  const pc = c.coste_por_cliente;
  return (
    <Bloque
      icono={Users}
      titulo="Coste por cliente"
      pregunta={{
        titulo: '¿Cómo se calcula?',
        texto: (
          <>
            El coste medio reparte todo lo gastado el último mes completo entre los clientes con
            cuenta. La lista de abajo es solo lo que cada cliente ha gastado en IA con sus
            estudios de factura, que es lo único que depende de cada uno. Los visitantes que
            prueban el estudio sin registrarse van juntos en una fila.
          </>
        ),
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Kpi
          label="Coste medio por cliente"
          value={pc ? eur(pc.usd, pc.usd * c.cambio.usd_eur < 0.1 ? 3 : 2) : '—'}
          nota={pc ? `${mesLabel(pc.mes)} · ${pc.clientes} clientes con cuenta` : 'Aún sin un mes completo'}
        />
        <Kpi
          label="Clientes con cuenta"
          value={fmt(c.ia.clientes_con_cuenta, 0)}
          nota="En el portal"
        />
      </div>
      {c.ia.por_cliente.length > 0 ? (
        <div className="mt-4 overflow-hidden rounded-2xl border border-deep/10">
          <table className="w-full text-sm">
            <thead className="bg-soft text-left text-[0.68rem] font-bold uppercase tracking-wider text-ink/50">
              <tr>
                <th className="px-4 py-2">Quién</th>
                <th className="px-4 py-2 text-right">Estudios</th>
                <th className="px-4 py-2 text-right">Coste IA</th>
              </tr>
            </thead>
            <tbody>
              {c.ia.por_cliente.map((f) => (
                <tr key={f.client_id ?? 'anon'} className="border-t border-deep/10">
                  <td className="px-4 py-2 font-semibold text-deep">{f.nombre}</td>
                  <td className="px-4 py-2 text-right text-ink/70">{f.estudios}</td>
                  <td className="px-4 py-2 text-right font-bold text-deep">{eur(f.coste_usd, 3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-3 text-sm text-ink/50">Ningún cliente ha hecho aún un estudio de factura.</p>
      )}
    </Bloque>
  );
}

// ── 7 · Lo que viene y pendientes ─────────────────────────────────

function LoQueViene({ c, eur }: { c: Costes; eur: Eur }) {
  // Con el mes normal, no con el fin de mes: un gasto puntual (como las fotos
  // del cielo de septiembre) no cambia en qué escalón está el negocio.
  const referencia = c.prevision?.mes_tipo ?? c.mes_actual?.uso ?? 0;
  const aqui =
    c.escenarios.find((e) => referencia <= e.max)?.clave ?? c.escenarios[c.escenarios.length - 1]?.clave;
  const p = c.presupuesto;
  const destinatarios = Array.from(new Set(p?.avisos.flatMap((a) => a.a) ?? []));
  const avisosAFlorent = destinatarios.some((d) => d.endsWith('@habitania.info'));

  const pendientes: { titulo: string; texto: string }[] = [];
  if (c.plan?.tipo === 'FREE' && c.plan.caduca)
    pendientes.push({
      titulo: `Pasar la cuenta a plan de pago antes del ${fechaLarga(c.plan.caduca)}`,
      texto: 'Con la tarjeta de Habitania. No cuesta nada y no hace perder los créditos.',
    });
  if (c.cif && !c.cif.registrado)
    pendientes.push({
      titulo: 'Poner el CIF de Habitania en AWS',
      texto:
        'Sin él, AWS cobra un 21 % de IVA en cada factura. Con él deja de cobrarlo (inversión del sujeto pasivo). Lo pone Adrien en cuanto tenga el CIF.',
    });
  if (destinatarios.length && !avisosAFlorent)
    pendientes.push({
      titulo: 'Recibir tú los avisos de gasto',
      texto: `Hoy los avisos por correo llegan a ${destinatarios.join(', ')}. Si quieres recibirlos también, díselo a Adrien.`,
    });

  return (
    <Bloque
      icono={CalendarClock}
      titulo="Lo que viene"
      pregunta={{
        titulo: '¿Puede llegar una factura sorpresa?',
        texto: (
          <>
            No. Se paga solo lo que se usa, y no hay nada encendido las 24 horas. Además hay un
            tope de aviso: si el gasto se acerca, AWS manda un correo antes de que pase nada (más
            abajo ves a quién le llega). Las horquillas de «Si el negocio crece» son lo que
            costaría la plataforma según crezca el negocio, sin IVA.
          </>
        ),
      }}
    >
      <ul className="space-y-2">
        {c.calendario.map((e) => (
          <li
            key={e.fecha + e.que}
            className={clsx(
              'flex gap-3 rounded-2xl border px-4 py-3',
              e.aviso ? 'border-amber-300 bg-amber-50' : 'border-deep/10 bg-surface',
            )}
          >
            <span className="w-28 shrink-0 text-xs font-bold uppercase tracking-wide text-ink/55">
              {fechaLarga(e.fecha)}
            </span>
            <div>
              <p className={clsx('flex items-center gap-1.5 text-sm font-bold', e.aviso ? 'text-amber-900' : 'text-deep')}>
                {e.aviso && <AlertTriangle size={14} />}
                {e.que}
              </p>
              <p className="text-xs text-ink/60">{e.detalle}</p>
            </div>
          </li>
        ))}
      </ul>

      <p className="mt-6 text-xs font-bold uppercase tracking-wider text-ink/45">
        Si el negocio crece
      </p>
      <div className="mt-2 grid gap-3 sm:grid-cols-3">
        {c.escenarios.map((e) => (
          <div
            key={e.clave}
            className={clsx(
              'rounded-2xl border px-4 py-3',
              e.clave === aqui ? 'border-brand bg-leaf/20' : 'border-deep/10 bg-surface',
            )}
          >
            {e.clave === aqui && (
              <p className="text-[0.65rem] font-bold uppercase tracking-wider text-brand">Tú estás aquí</p>
            )}
            <p className="text-sm font-bold text-deep">{e.nombre}</p>
            <p className="text-lg font-extrabold text-deep">
              {eur(e.min, 0)} – {eur(e.max, 0)}
              <span className="text-xs font-semibold text-ink/45"> /mes</span>
            </p>
            <p className="text-xs text-ink/60">{e.detalle}</p>
          </div>
        ))}
      </div>

      {p && (
        <p className="mt-4 rounded-xl bg-soft px-4 py-3 text-sm text-ink/70">
          Tope de aviso: <strong className="text-deep">{fmt(p.limite, 0)} $ al mes</strong> (≈{' '}
          {eur(p.limite, 0)}). AWS avisa por correo si el gasto real pasa del{' '}
          {p.avisos.find((a) => a.tipo === 'real')?.umbral_pct ?? 80} % o si la previsión pasa del{' '}
          {p.avisos.find((a) => a.tipo === 'previsto')?.umbral_pct ?? 100} %. No corta nada: solo
          avisa.
        </p>
      )}

      {pendientes.length > 0 && (
        <>
          <p className="mt-6 text-xs font-bold uppercase tracking-wider text-ink/45">
            Pendiente en la cuenta
          </p>
          <ul className="mt-2 space-y-2">
            {pendientes.map((x) => (
              <li key={x.titulo} className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3">
                <p className="text-sm font-bold text-amber-900">{x.titulo}</p>
                <p className="text-xs text-amber-900/80">{x.texto}</p>
              </li>
            ))}
          </ul>
        </>
      )}
    </Bloque>
  );
}

// ── Piezas ────────────────────────────────────────────────────────

function Actualizar({ data }: { data: Costes }) {
  const act = useActualizarCostes();
  const [nota, setNota] = useState<string | null>(null);
  return (
    <div className="text-right">
      <button
        type="button"
        disabled={act.isPending}
        onClick={() =>
          act.mutate(undefined, {
            onSuccess: (nuevo) =>
              setNota(
                nuevo.leido_en === data.leido_en
                  ? 'Ya estaba al día: se consulta como mucho una vez por hora.'
                  : null,
              ),
            onError: () => setNota('No se ha podido actualizar. Inténtalo más tarde.'),
          })
        }
        className="inline-flex items-center gap-2 rounded-full border border-deep/15 px-4 py-2 text-sm font-bold text-deep transition hover:border-brand hover:text-brand disabled:opacity-50"
      >
        <RefreshCw size={15} className={clsx(act.isPending && 'animate-spin')} />
        {act.isPending ? 'Consultando AWS…' : 'Actualizar'}
      </button>
      <p className="mt-1 text-xs text-ink/45">Datos de AWS {haceCuanto(data.leido_en)}</p>
      {nota && <p className="mt-1 max-w-xs text-xs text-ink/55">{nota}</p>}
    </div>
  );
}

function Bloque({
  icono: Icono,
  titulo,
  pregunta,
  children,
}: {
  icono: LucideIcon;
  titulo: string;
  pregunta?: { titulo: string; texto: ReactNode };
  children: ReactNode;
}) {
  return (
    <section className="mt-6 rounded-2xl border border-deep/10 bg-surface p-5">
      <div className="mb-4">
        <h2 className="flex items-center gap-2 text-base font-extrabold text-deep">
          <Icono size={18} className="text-brand" />
          {titulo}
        </h2>
        {pregunta && <Pregunta titulo={pregunta.titulo}>{pregunta.texto}</Pregunta>}
      </div>
      {children}
    </section>
  );
}

function Kpi({ label, value, nota }: { label: string; value: string; nota?: string }) {
  return (
    <div className="rounded-2xl border border-deep/10 bg-surface px-4 py-3">
      <p className="text-xl font-extrabold text-deep">{value}</p>
      <p className="text-[0.68rem] font-bold uppercase tracking-wider text-ink/45">{label}</p>
      {nota && <p className="mt-1 text-xs text-ink/55">{nota}</p>}
    </div>
  );
}

function Aviso({ children }: { children: ReactNode }) {
  return (
    <div className="mt-4 flex gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <AlertTriangle size={18} className="mt-0.5 shrink-0" />
      <p>{children}</p>
    </div>
  );
}

function Vacio({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-2xl border border-dashed border-deep/20 px-6 py-8 text-center text-sm text-ink/60">
      {children}
    </p>
  );
}
